import { UPLOAD_LIMITS } from './config/uploadLimits.js';
import { CONCURRENCY_CONFIG, RESIZE_CONFIG } from './config/uploads.js';
import { UI_COPY } from './config/uiCopy.js';

export function detectConcurrency() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const ram = navigator.deviceMemory || 0; // undefined on Safari/Firefox
    const cores = navigator.hardwareConcurrency || 2;
    if (isMobile) return CONCURRENCY_CONFIG.MOBILE_MAX; // Always 1 for mobile to prevent tab crash
    if (ram >= CONCURRENCY_CONFIG.HIGH_RAM_GB) return CONCURRENCY_CONFIG.HIGH_RAM_CONCURRENCY;
    if (ram >= CONCURRENCY_CONFIG.MID_RAM_GB) return CONCURRENCY_CONFIG.MID_RAM_CONCURRENCY;

    // Fallback for Safari Desktop (unknown RAM)
    if (cores >= CONCURRENCY_CONFIG.DESKTOP_CORE_FALLBACK) return CONCURRENCY_CONFIG.DESKTOP_CONCURRENCY;
    return CONCURRENCY_CONFIG.DEFAULT_CONCURRENCY; // Conservative default
}

export async function resizeImageToFile(file, maxSide = RESIZE_CONFIG.MAX_SIDE_PX) {
    let bmp;
    try {
        bmp = await createImageBitmap(file);
    } catch(e) {
        // If browser can't read it (e.g. TIFF/HEIC sometimes), return original
        return file;
    }

    let w = bmp.width, h = bmp.height;

    // --- NEW: Pixel Dimension Check (Decompression Bomb Protection) ---
    const pixels = w * h;
    // We assume "Tile" limits here because this is mostly used for library uploads.
    // Ideally, pass a flag if it's a target, but 100MP is a safe upper bound for browser resizing anyway.
    if (pixels > UPLOAD_LIMITS.MAX_TILE_PIXELS) {
        bmp.close();
        const mp = (pixels / 1_000_000).toFixed(1);
        const limitMp = (UPLOAD_LIMITS.MAX_TILE_PIXELS / 1_000_000).toFixed(0);
        throw new Error(UI_COPY.uploads.imageTooLarge(mp, limitMp));
    }
    // ------------------------------------------------------------------

    // If small enough, send original
    if (!(w > maxSide || h > maxSide)) {
        bmp.close();
        // Cleanup memory
        return file;
    }

    // Calculate Aspect Ratio
    if (w > h) {
        h = Math.round(h * (maxSide / w));
        w = maxSide;
    } else {
        w = Math.round(w * (maxSide / h));
        h = maxSide;
    }

    const cvs = document.createElement('canvas');
    cvs.width = w; cvs.height = h;
    const ctx = cvs.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    // Important: Release Bitmap memory immediately

    const originalBase = (file.name || "tile").replace(/\.[^/.]+$/, "");
    const outName = `${originalBase}.jpg`;
    return new Promise(resolve => {
        cvs.toBlob(blob => {
            // Convert Blob -> File
            if (!blob) { resolve(file); return; } // Fallback
            const outFile = new File([blob], outName, { type: 'image/jpeg' });
            resolve(outFile);
        }, 'image/jpeg', RESIZE_CONFIG.JPEG_QUALITY);
    });
}
